import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { api, type MapSettings, type PinnedSpot, type Place } from '../lib/api'
import { addBasemap, mapSettings } from '../lib/basemap'

/** Where an address is, on a map.
 *
 *  WHAT THIS IS FOR
 *
 *  Confirming the address before people are sent to it. A dispatcher reads
 *  "42 Oak Street" back to a caller and it sounds right; seeing it land in the
 *  wrong borough is how he finds out it was not. That is the whole job today —
 *  responder positions and ETA come later, with Traccar.
 *
 *  AN EMPTY MAP IS NOT A BLANK SPACE
 *
 *  A place with no coordinates renders a SENTENCE saying why, never an empty
 *  grey square. The two states a dispatcher must be able to tell apart are
 *  "still loading" and "the map does not know this address" — the second one
 *  means stop waiting and ask for a cross street, and a blank box says neither.
 *
 *  NOTHING IS LOOKED UP WHILE SOMEBODY TYPES
 *
 *  OpenStreetMap's usage policy names autocomplete as a way to get an
 *  application blocked, so the lookup is a button and an intake-time background
 *  task, never a keystroke handler. See app/geocoding/nominatim.py. */

/** Leaflet's default marker is a PNG it resolves relative to its own stylesheet,
 *  which a bundler rewrites and then cannot find — the classic symptom is a
 *  working map with an invisible pin. An inline SVG has no asset to lose. */
const PIN = L.divIcon({
  className: 'place-pin',
  html:
    '<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">' +
    '<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z" ' +
    'fill="#d33a3a" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>',
  iconSize: [26, 34],
  iconAnchor: [13, 34],
})

export function PlaceMap({
  place,
  height = 220,
  onLocated,
  pinned = null,
  readOnly = false,
}: {
  place: Place | null
  height?: number
  /** Handed the updated place after a successful lookup, so the card around
   *  this one can redraw its address line from what the map returned. */
  onLocated?: (place: Place) => void
  /** THE SPOT A DISPATCHER PINNED ON THIS CALL, if he moved it at intake
   *  (migration 053). It WINS over the address's own coordinates, because it
   *  is a person saying "go to the loading bay behind it" and the geocoder's
   *  answer is about the building — and it is captioned as his, so nobody
   *  reads a human's correction as the map's own answer. */
  pinned?: PinnedSpot | null
  /** Past calls: draws what is known and offers no lookup. A lookup sends the
   *  address to the map provider and rewrites the ADDRESS, which reviewing
   *  a closed call must never do. */
  readOnly?: boolean
}) {
  const box = useRef<HTMLDivElement | null>(null)
  const map = useRef<L.Map | null>(null)
  const pin = useRef<L.Marker | null>(null)
  const detach = useRef<(() => void) | null>(null)
  /** Watches the map's own box (22 Sep 2026). Leaflet only watches the
   *  WINDOW for resizes, and this map now lives on the Caller tab while a
   *  broadcast call opens on Dispatch: it is created in a box with no size
   *  and revealed later, which is not a window resize. */
  const watch = useRef<ResizeObserver | null>(null)
  const [settings, setSettings] = useState<MapSettings | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    mapSettings().then((s) => { if (alive) setSettings(s) })
    return () => { alive = false }
  }, [])

  const lat = pinned?.latitude ?? place?.latitude ?? null
  const lon = pinned?.longitude ?? place?.longitude ?? null

  useEffect(() => {
    if (!settings?.enabled || !settings.tile_url) return
    if (lat === null || lon === null) return
    if (!box.current) return

    if (!map.current) {
      map.current = L.map(box.current, {
        // A dispatcher is on the phone and will not be pinch-zooming. Scroll
        // wheel off, because a page scrolled past the map should scroll the
        // page rather than silently zoom out to the whole province.
        scrollWheelZoom: false,
        attributionControl: true,
      })
      // OpenStreetMap or Google, by the organization's map level, with the
      // right credit for whichever it is. lib/basemap.ts.
      detach.current = addBasemap(map.current, settings, setNote)
      // REVEALED LATER IS NOT RESIZED. The one-shot below fires while the
      // Caller tab is still hidden; this asks Leaflet to measure again the
      // moment the box gets a size -- the tab opening, or the column
      // narrowing past the 1000px breakpoint where the cards stack.
      if (typeof ResizeObserver !== 'undefined') {
        watch.current = new ResizeObserver(() => map.current?.invalidateSize())
        watch.current.observe(box.current)
      }
    }

    map.current.setView([lat, lon], 17)
    if (pin.current) pin.current.setLatLng([lat, lon])
    else pin.current = L.marker([lat, lon], { icon: PIN }).addTo(map.current)

    // The container is created hidden inside a card that may have been laid
    // out after this ran; without this Leaflet keeps the size it measured and
    // renders a strip of tiles in the corner.
    const t = setTimeout(() => map.current?.invalidateSize(), 60)
    return () => clearTimeout(t)
  }, [settings, lat, lon])

  useEffect(() => () => {
    watch.current?.disconnect()
    watch.current = null
    detach.current?.()
    detach.current = null
    map.current?.remove()
    map.current = null
    pin.current = null
  }, [])

  // A pin with no place is still a place on a map: a call can be pinned to a
  // spot whose address was typed and never matched to a record.
  if (!place && !pinned) return null

  if (settings && !settings.enabled) {
    return (
      <p className="map-note">
        No map is configured for this organization.
      </p>
    )
  }

  async function find(force: boolean) {
    if (!place) return
    setLooking(true)
    setSaid(null)
    try {
      const out = await api.placeLocate(place.id, force)
      if (!out.located) setSaid(out.detail || 'The map could not find it.')
      onLocated?.(out.place)
    } catch {
      setSaid('The lookup could not be run.')
    } finally {
      setLooking(false)
    }
  }

  if (lat === null || lon === null) {
    // Deliberately a sentence and not an empty box. "Never looked up" and
    // "looked up and not found" are the same absence of coordinates and need
    // opposite things from the dispatcher.
    if (!place) return null
    const why = said || place.geocode_error
    return (
      <div className="map-note">
        <p>
          {why
            ? why
            : place.geocoded_at
              ? 'The map has no location for this address.'
              : 'This address has not been looked up on the map yet.'}
        </p>
        {!readOnly && (
          <button type="button" onClick={() => find(false)} disabled={looking}>
            {looking ? 'Looking…' : 'Find on map'}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="placemap">
      <div ref={box} style={{ height }} className="placemap-canvas" />
      {/* Why this is not the Google map the level promises. */}
      {note && <p className="map-note">{note}</p>}
      {/* WHOSE PIN THIS IS. A dispatcher moved it while the caller was on the
          phone, so it is his claim about where to go and not the map's answer
          about the address — and it is worth saying, because the two look
          identical on a map and only one of them was agreed with somebody who
          was standing there. */}
      {pinned && (
        <p className="map-pinned">
          Pin placed by {pinned.by || 'the dispatcher'}
          {pinned.at ? ` at ${new Date(pinned.at).toLocaleTimeString([], {
            hour: '2-digit', minute: '2-digit',
          })}` : ''} — not the map’s own answer for the address.
        </p>
      )}
      {place?.formatted_address && !pinned && (
        // Shown because it is the one cheap way to catch the right street in
        // the wrong town: if this does not read like the address that was
        // typed, the pin is somewhere else.
        <p className="map-found">Map has: {place.formatted_address}</p>
      )}
      {said && <p className="map-note">{said}</p>}
      {place && !readOnly && (
        <button type="button" className="linkish" onClick={() => find(true)}
                disabled={looking}>
          {looking ? 'Looking…' : 'Look up again'}
        </button>
      )}
    </div>
  )
}
